import React, { useState, useEffect, useRef } from "react";
import {
  createOutward,
  updateOutward,
  fetchDocumentTypes,
  fetchDispatchModes,
} from "../api/inwardOutwardApi";
import { fetchUsersOptions } from "../../user-builder/api/userBuilderApi";
import { getCsrfToken } from "../../../public/api/authApi";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import { gfValidateNameTextField } from "../../../global/forms/globalFormValidation";
import UserSearch from "./UserSearch";
import GlobalFormShell from "../../../global/forms/GlobalFormShell";
import { masterSelectOptions, filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";
import {
  INWARD_OUTWARD_TEXT_MAX,
  ALLOWED_UPLOAD_TYPES,
  INVALID_UPLOAD_TYPE_MESSAGE,
  validateInwardOutwardTextField,
} from "../utils/inwardOutwardFormConstants";
import { compressAttachmentIfImage } from "../../../utils/compressImage";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

const EMPTY_FORM = {
  document_type_id: "",
  subject: "",
  dispatch_mode_id: "",
  remarks: "",
  attachment: "",
  date_sent: new Date().toISOString().split("T")[0],
  to_name: "",
  to_address: "",
  dispatched_by: "",
  acknowledgement_no: "",
};
const buildAttachmentUrl = (attachmentPath) => resolveMediaUrl(attachmentPath);

const getAttachmentName = (attachmentPath) => {
  if (!attachmentPath || typeof attachmentPath !== "string") return "";
  const cleanPath = attachmentPath.split("?")[0];
  const parts = cleanPath.split("/");
  return parts[parts.length - 1] || cleanPath;
};

const formatDateForInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const OutwardEntryForm = ({ onSuccess, lookupsRefreshKey = 0, editingRecord, onCancelEdit }) => {
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [attachment, setAttachment] = useState(null);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState({});
  const [loading, setLoading] = useState(false);
  
  const [lookups, setLookups] = useState({
    documentTypes: [],
    dispatchModes: [],
    users: [],
  });

  const alertRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [docsRes, dispatchRes, usersOptions] = await Promise.all([
          fetchDocumentTypes({ scope: "dropdown" }),
          fetchDispatchModes({ scope: "dropdown" }),
          fetchUsersOptions(1000),
        ]);
        setLookups({
          documentTypes: filterActiveMasterRows(docsRes.data || []),
          dispatchModes: filterActiveMasterRows(dispatchRes.data || []),
          users: usersOptions || [],
        });
      } catch (err) {
        console.error("Failed to load lookups for register form", err);
      }
    };
    loadLookups();
  }, [lookupsRefreshKey]);

  useEffect(() => {
    if (editingRecord) {
      setFormData({
        ...EMPTY_FORM,
        ...editingRecord,
        date_sent: formatDateForInput(editingRecord.date_sent),
      });
      setAttachment(null);
      setError(null);
      setErrorDetails({});
    } else {
      handleReset();
    }
  }, [editingRecord]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorDetails[name]) {
      setErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleReset = () => {
    setError(null);
    setErrorDetails({});
    setFormData({ ...EMPTY_FORM });
    setAttachment(null);
    // Clear the file input field
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateDateNotFuture = (dateString) => {
    if (!dateString) return true;
    const inputDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);
    return inputDate <= today;
  };

  const validateAll = () => {
    const errors = {};
    if (!formData.document_type_id) errors.document_type_id = "Required";
    const subjectErr = validateInwardOutwardTextField(formData.subject, "Subject");
    if (subjectErr) errors.subject = subjectErr;
    if (!formData.dispatch_mode_id) errors.dispatch_mode_id = "Required";
    const remarksErr = validateInwardOutwardTextField(formData.remarks, "Remarks");
    if (remarksErr) errors.remarks = remarksErr;
    if (!attachment && !editingRecord) errors.attachment = "Required";
    if (!attachment && editingRecord && !editingRecord.attachment) {
      errors.attachment = "Required";
    }
    
    if (!formData.date_sent) errors.date_sent = "Required";
    else if (!validateDateNotFuture(formData.date_sent)) errors.date_sent = "Date cannot be in the future";
    const toName = String(formData.to_name || "").trim();
    if (!toName) {
      errors.to_name = "Required";
    } else if (toName.length > 30) {
      errors.to_name = "Maximum 30 characters.";
    } else {
      const nameError = gfValidateNameTextField(toName);
      if (nameError) errors.to_name = nameError;
    }
    const addressErr = validateInwardOutwardTextField(formData.to_address, "To address");
    if (addressErr) errors.to_address = addressErr;
    if (!formData.dispatched_by) errors.dispatched_by = "Required";
    setErrorDetails(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setErrorDetails({});
    
    if (!validateAll()) {
      setError("Please check the highlighted fields below.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      
      let payload = { ...formData };
      delete payload.acknowledgement_no;

      // Convert to FormData
      const formDataToSend = new FormData();
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== undefined && payload[key] !== null && payload[key] !== "") {
          formDataToSend.append(key, payload[key]);
        }
      });
      if (attachment) {
        formDataToSend.append("file", attachment);
      }

      const isEditing = !!editingRecord;
      let result;
      if (isEditing) {
        result = await updateOutward(editingRecord.id, formDataToSend, csrfToken);
      } else {
        result = await createOutward(formDataToSend, csrfToken);
      }

      if (result.error) {
        let errorMsg = typeof result.error.error === "string" 
          ? result.error.error 
          : (typeof result.error.message === "string" ? result.error.message : "Failed to create outward entry");
        setErrorDetails(result.error.details && typeof result.error.details === "object" ? result.error.details : {});
        
        setError(errorMsg);
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }
      
      onSuccess?.(`Outward entry ${isEditing ? "updated" : "created"} successfully!`);
      if (isEditing) {
        onCancelEdit?.();
      } else {
        handleReset();
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

  const ALERT_ID = "office-outward-form-alert";

  return (
    <GlobalFormShell
      legend={editingRecord ? "Edit Outward Entry" : "New Outward Entry"}
      showAlert={!!error}
      alertText={error}
      alertId={ALERT_ID}
      formAlertRef={alertRef}
      actions={
        <>
          <GlobalButton
            type="submit"
            variant="primary"
            disabled={loading}
            onClick={handleSubmit}
            loading={loading}
          >
            {editingRecord ? "Save Changes" : "Save Outward Entry"}
          </GlobalButton>
          {editingRecord ? (
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={() => onCancelEdit?.()}
              disabled={loading}
            >
              Cancel
            </GlobalButton>
          ) : (
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={handleReset}
              disabled={loading}
            >
              Clear form
            </GlobalButton>
          )}
        </>
      }
    >
      <div className="gff-field--full" style={{ marginBottom: "-0.5rem" }}>
        <h4 style={{ margin: "0.5rem 0", color: "var(--ub-teal-dark)", fontSize: "0.85rem", fontWeight: 700 }}>
          Recipient Information
        </h4>
      </div>
      
      <GlobalTextField
        id="date_sent"
        label="Date Sent"
        type="date"
        value={formData.date_sent}
        onChange={(v) => setFormField("date_sent", v)}
        error={errorDetails.date_sent}
        baseAlertId={ALERT_ID}
        required
        max={new Date().toISOString().split("T")[0]}
      />
      <GlobalTextField
        id="to_name"
        label="To Name"
        value={formData.to_name}
        onChange={(v) => setFormField("to_name", v)}
        error={errorDetails.to_name}
        baseAlertId={ALERT_ID}
        nameTextRules
        maxLength={30}
        required
      />
      <GlobalTextAreaField
        classNames={{ fieldWrap: "gff-field gff-field--full" }}
        id="to_address"
        label="To Address"
        value={formData.to_address}
        onChange={(v) => setFormField("to_address", v)}
        error={errorDetails.to_address}
        baseAlertId={ALERT_ID}
        maxLength={INWARD_OUTWARD_TEXT_MAX}
        required
      />

      <div className="gff-field--full" style={{ marginBottom: "-0.5rem", marginTop: "0.5rem" }}>
        <h4 style={{ margin: "0.5rem 0", color: "var(--ub-teal-dark)", fontSize: "0.85rem", fontWeight: 700 }}>
          Document Details
        </h4>
      </div>

      <GlobalSelectField
        id="document_type_id"
        label="Document Type"
        value={formData.document_type_id}
        onChange={(v) => setFormField("document_type_id", v ? Number(v) : "")}
        options={masterSelectOptions(lookups.documentTypes, {
          labelKey: "type_name",
          selectedValue: formData.document_type_id,
        })}
        error={errorDetails.document_type_id}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalSelectField
        id="dispatch_mode_id"
        label="Mode of Dispatch"
        value={formData.dispatch_mode_id}
        onChange={(v) => setFormField("dispatch_mode_id", v ? Number(v) : "")}
        options={masterSelectOptions(lookups.dispatchModes, {
          labelKey: "mode_name",
          selectedValue: formData.dispatch_mode_id,
        })}
        error={errorDetails.dispatch_mode_id}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalTextAreaField
        classNames={{ fieldWrap: "gff-field gff-field--full" }}
        id="subject"
        label="Subject"
        value={formData.subject}
        onChange={(v) => setFormField("subject", v)}
        error={errorDetails.subject}
        baseAlertId={ALERT_ID}
        maxLength={INWARD_OUTWARD_TEXT_MAX}
        required
      />
      
      <GlobalTextField
        id="acknowledgement_no"
        label="Acknowledgement No"
        value={formData.acknowledgement_no}
        onChange={() => {}}
        error={errorDetails.acknowledgement_no}
        baseAlertId={ALERT_ID}
        placeholder={editingRecord ? "" : "Auto-generated on save"}
        disabled
      />
      
      <div className="gff-field--full" style={{ marginBottom: "-0.5rem", marginTop: "0.5rem" }}>
        <h4 style={{ margin: "0.5rem 0", color: "var(--ub-teal-dark)", fontSize: "0.85rem", fontWeight: 700 }}>
          Additional Information
        </h4>
      </div>

      <UserSearch
        id="dispatched_by"
        label="Dispatched By (Search)"
        value={formData.dispatched_by}
        onChange={(v) => setFormField("dispatched_by", v ? Number(v) : "")}
        selectedUserLabel={editingRecord?.dispatched_by_name || editingRecord?.name}
        error={errorDetails.dispatched_by}
        baseAlertId={ALERT_ID}
        required
      />
      
      <p className="gff-field gff-field--full">
        <label className={`gff-label${errorDetails.attachment ? " gff-label--error" : ""}`} htmlFor="attachment">
          Upload Document {!editingRecord ? <span aria-hidden="true">*</span> : null}
        </label>
        <div
          className={`gff-input${errorDetails.attachment ? " gff-input--error" : ""}`}
          style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 0.5rem" }}
        >
          <input
            id="attachment"
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
            onChange={async (e) => {
              const file = e.target.files?.[0] || null;
              if (!file) {
                setAttachment(null);
                return;
              }
              if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
                setAttachment(null);
                e.target.value = "";
                setErrorDetails((prev) => ({ ...prev, attachment: INVALID_UPLOAD_TYPE_MESSAGE }));
                return;
              }
              try {
                const uploadFile = await compressAttachmentIfImage(file);
                setAttachment(uploadFile);
                if (errorDetails.attachment) setErrorDetails((prev) => ({ ...prev, attachment: "" }));
              } catch {
                setAttachment(null);
                e.target.value = "";
                setErrorDetails((prev) => ({ ...prev, attachment: "Could not compress image" }));
              }
            }}
            required={!editingRecord}
            aria-invalid={errorDetails.attachment ? "true" : "false"}
            aria-describedby={errorDetails.attachment ? "attachment-error" : undefined}
          />
          <GlobalButton type="button" variant="ghost" onClick={() => fileInputRef.current?.click()}>
            Choose File
          </GlobalButton>
          <span className="gff-help">
            {attachment ? (
              <>New file selected: {attachment.name}</>
            ) : editingRecord?.attachment ? (
              <a
                href={buildAttachmentUrl(editingRecord.attachment)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#15803d" }}
              >
                {getAttachmentName(editingRecord.attachment)}
              </a>
            ) : (
              "No file chosen"
            )}
          </span>
        </div>
        {errorDetails.attachment ? (
          <span id="attachment-error" className="gff-error" role="alert">
            {errorDetails.attachment}
          </span>
        ) : null}
      </p>

      <GlobalTextAreaField
        classNames={{ fieldWrap: "gff-field gff-field--full" }}
        id="remarks"
        label="Remarks"
        value={formData.remarks}
        onChange={(v) => setFormField("remarks", v)}
        error={errorDetails.remarks}
        baseAlertId={ALERT_ID}
        maxLength={INWARD_OUTWARD_TEXT_MAX}
        required
      />
    </GlobalFormShell>
  );
};

export default OutwardEntryForm;
